/**
 * Speech scores for one spoken answer, normalised from Stimuler's speech analysis (RayServe unified v3). Delivery
 * (pronunciation, fluency) and language (grammar, vocabulary) sit next to the content feedback from the text LLM.
 */

export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export type Cefr = (typeof CEFR_LEVELS)[number];

/** Upper-inclusive bands, as in Stimuler's app (backend-2 overall-score-cefr.ts). */
export function scoreToCefr(score: number | null): Cefr | null {
  if (score === null || !Number.isFinite(score) || score < 0 || score > 100) return null;
  if (score <= 40) return 'A1';
  if (score <= 60) return 'A2';
  if (score <= 70) return 'B1';
  if (score <= 80) return 'B2';
  if (score <= 90) return 'C1';
  return 'C2';
}

/** Why an answer has no scores. The coach and the card say something different for each. */
export type NotScoredReason =
  | 'unclear' // speech wasn't comprehensible (or wasn't English)
  | 'off_topic' // didn't answer the question
  | 'repeated' // repeated the question back
  | 'capped' // daily scoring limit reached
  | 'unavailable'; // analysis failed or timed out

export type SpeechScores =
  | { status: 'not_scored'; reason: NotScoredReason }
  | {
      status: 'scored';
      overall: number;
      cefr: Cefr | null;
      pronunciation: { score: number; accuracy: number; fluency: number; prosody: number; completeness: number } | null;
      fluency: { score: number; wpm: number | null; pauses: number; fillers: string[] } | null;
      grammar: {
        score: number;
        band: string | null;
        edits: Array<{ category: string; original: string; corrected: string }>;
      } | null;
      vocabulary: {
        score: number;
        cefr: Record<string, number>;
        suggestions: Array<{ original: string; replacement: string; level: string | null }>;
      } | null;
      /** Lowest-accuracy words (below 60), worst first, at most 5. */
      /** `at`: the word's place among the words meant to be said, i.e. in a read-aloud's script. */
      weakWords: Array<{ word: string; accuracy: number; at?: number }>;
      /** Mean accuracy per phoneme (Stimuler's IPA inventory) in this answer. */
      phonemes: Array<{ phoneme: string; accuracy: number; count: number }>;
    };

/** Plain-language reason, for the card (and the coach) when an answer isn't scored. */
export const NOT_SCORED_TEXT: Record<NotScoredReason, string> = {
  unclear: "I couldn't hear it clearly enough to score.",
  off_topic: "It didn't sound like an answer to the question, so I didn't score it.",
  repeated: 'It sounded like the question read back, so I didn’t score it.',
  capped: 'Scoring is paused for today (daily limit reached).',
  unavailable: 'Scoring didn’t work this time.',
};

/**
 * The compact version the coach gets in the speaking_task function response: enough to name one delivery and one
 * language point, without the per-phoneme detail that belongs on screen.
 */
export function scoresForCoach(s: SpeechScores) {
  if (s.status === 'not_scored') return { scored: false, reason: NOT_SCORED_TEXT[s.reason] };
  return {
    scored: true,
    overall: s.overall,
    cefr: s.cefr,
    pronunciation: s.pronunciation?.score ?? null,
    fluency: s.fluency
      ? {
          score: s.fluency.score,
          wordsPerMinute: s.fluency.wpm,
          longPauses: s.fluency.pauses,
          fillers: s.fluency.fillers,
        }
      : null,
    grammar: s.grammar
      ? { score: s.grammar.score, fixes: s.grammar.edits.map((e) => `${e.original} -> ${e.corrected}`) }
      : null,
    vocabulary: s.vocabulary
      ? {
          score: s.vocabulary.score,
          betterWords: s.vocabulary.suggestions.map((v) => `${v.original} -> ${v.replacement}`),
        }
      : null,
    weakSounds: weakSounds(s).map((p) => p.phoneme),
    unclearWords: s.weakWords.slice(0, 3).map((w) => w.word),
  };
}

/** Sounds to practise: below 60, heard at least twice in the answer (one bad instance is noise), worst first. */
export function weakSounds(s: Extract<SpeechScores, { status: 'scored' }>) {
  return s.phonemes.filter((p) => p.accuracy < 60 && p.count >= 2).slice(0, 3);
}
